import { debugLog } from 'shared/logger/debugLog';
import {
  ConfigData,
  clearTokenExpiration,
  get as getDbConfig,
  setTokenExpiration,
} from 'serviceWorker/db/config';
import { swSignals } from 'serviceWorker/events/swSignals';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import { tokenClient } from 'shared/domain/auth/tokenClient';
import { isFreshToken, TOKEN_FRESHNESS_THRESHOLD_MS } from 'shared/domain/auth/tokenModel';
import { requestTokenFromPage } from './requestTokenFromPage';
import {
  ServiceWorkerTokenRequestError,
  shouldEnterAuthCooldownOnTokenRequestError,
} from './tokenRequestError';

const AUTH_COOLDOWN_MS = 30_000;

/** In-memory only — bearer token is never persisted; only expiration is stored in IDB. */
let authCooldownUntil = 0;

function isAuthCooldownActive(): boolean {
  return Date.now() < authCooldownUntil;
}

function enterAuthCooldown(reason: string): void {
  authCooldownUntil = Date.now() + AUTH_COOLDOWN_MS;
  swLog(
    'SW auth cooldown entered',
    LogLevel.INFO,
    new Error(reason),
    {
      setup: {
        reason,
        cooldownMs: AUTH_COOLDOWN_MS,
        authCooldownUntil,
      },
    }
  );
}

function clearAuthCooldown(): void {
  authCooldownUntil = 0;
}

export function canMakeAuthenticatedRequests(): boolean {
  return !isAuthCooldownActive();
}

export function getAuthCooldownRemainingMs(): number {
  return Math.max(0, authCooldownUntil - Date.now());
}

function hasUsableInMemoryToken(
  hasToken: boolean,
  expiration: number
): boolean {
  return hasToken && expiration > Date.now();
}

function applyTokenPayload(
  tokenHolder: TokenHolder,
  token: string,
  tokenExpiration: number
): void {
  tokenHolder.setToken(token);
  tokenHolder.setExpiration(tokenExpiration);
  clearAuthCooldown();
  setTokenExpiration(tokenExpiration).catch(() => undefined);
}

class TokenHolder {
  private expiration = 0;
  private token = '';
  private readonly stopListeningToPushes: () => void;

  constructor() {
    swSignals.loggedOut.subscribe(() => {
      this.setToken('');
      this.setExpiration(0);
      clearAuthCooldown();
      clearTokenExpiration().catch(() => undefined);
    });

    this.stopListeningToPushes = tokenClient.onTokenPush((pushed) => {
      if (isFreshToken(pushed)) {
        applyTokenPayload(this, pushed.token, pushed.tokenExpiration);
      }
    });
  }

  destroy(): void {
    this.stopListeningToPushes();
  }

  setToken(token: string): void {
    this.token = token;
  }
  getToken(): string {
    return this.token;
  }
  getExpiration(): number {
    return this.expiration;
  }
  setExpiration(expiration: number): void {
    this.expiration = expiration;
  }
}

class TokenRequester {
  private awaiting = false;
  private currentPromise: Promise<void> = Promise.resolve();

  constructor(private tokenHolder: TokenHolder) {}

  onTokenExpired(): Promise<void> {
    if (this.awaiting) {
      return this.currentPromise;
    }
    this.awaiting = true;
    this.currentPromise = this.awaitToken().finally(() => {
      this.awaiting = false;
    });
    return this.currentPromise;
  }

  private async awaitToken(): Promise<void> {
    const answer = await requestTokenFromPage();
    if (answer.status === 'refused') {
      throw new ServiceWorkerTokenRequestError(answer.reason);
    }
    if (!isFreshToken(answer)) {
      throw new ServiceWorkerTokenRequestError('stale_token');
    }
    applyTokenPayload(this.tokenHolder, answer.token, answer.tokenExpiration);
  }
}

const tokenHolder = new TokenHolder();
const tokenRequester = new TokenRequester(tokenHolder);

(async function seedExpirationFromDb(): Promise<void> {
  try {
    const persisted = await getDbConfig();
    const exp = persisted?.tokenExpiration;
    if (exp && exp > Date.now() + TOKEN_FRESHNESS_THRESHOLD_MS) {
      tokenHolder.setExpiration(exp);
    }
  } catch {
    // Swallow - DB may not be ready yet on first install.
  }
})();

/** Injected into request runners, so tests can swap the source. */
export type AuthenticatedConfigProvider = () => Promise<ConfigData | undefined>;

/** Why the worker has no usable config: each one needs another way out. */
export enum ConfigUnavailableReason {
  /** The page did not deliver a token and the worker waits before asking again. */
  authCooldown = 'auth-cooldown',
  /** The page was asked for a token and did not give a usable one. */
  tokenRequestFailed = 'token-request-failed',
  /** The token is fine, but no config row is stored (nothing sent by the page yet, or the storage was lost). */
  configNotStored = 'config-not-stored',
}

export type ConfigResolution =
  | { config: ConfigData; reason?: undefined }
  | { config: undefined; reason: ConfigUnavailableReason };

/** Set once this worker instance saw a config: a missing one after that means the storage lost it. */
let hasSeenConfigInThisWorker = false;

export function hadConfigInThisWorker(): boolean {
  return hasSeenConfigInThisWorker;
}

async function readStoredConfig(): Promise<ConfigResolution> {
  const config = await getDbConfig();
  if (!config) {
    return { config: undefined, reason: ConfigUnavailableReason.configNotStored };
  }
  hasSeenConfigInThisWorker = true;
  return { config };
}

let inflightConfig: Promise<ConfigResolution> | null = null;

/**
 * Makes sure the SW holds a valid token (refreshing it via the page after a SW
 * restart, honouring the auth cooldown) and returns the stored config, or why
 * there is none. Call before any authenticated request – `generateHeaders`
 * reads the token synchronously.
 */
export function resolveAuthenticatedConfig(): Promise<ConfigResolution> {
  if (inflightConfig) {
    return inflightConfig;
  }
  inflightConfig = (async (): Promise<ConfigResolution> => {
    try {
      const expiration = tokenHolder.getExpiration();
      const nowPlusThreshold = Date.now() + TOKEN_FRESHNESS_THRESHOLD_MS;
      const hasToken = !!tokenHolder.getToken();
      const needsRefresh =
        !expiration || expiration < nowPlusThreshold || !hasToken;

      if (needsRefresh) {
        if (isAuthCooldownActive()) {
          if (hasUsableInMemoryToken(hasToken, expiration)) {
            return await readStoredConfig();
          }

          swLog(
            'SW ensureAuthenticatedConfig skipped: auth cooldown',
            LogLevel.INFO,
            null,
            {
              setup: {
                authCooldownUntil,
                remainingMs: getAuthCooldownRemainingMs(),
                hasToken,
                expiration,
              },
            }
          );
          return { config: undefined, reason: ConfigUnavailableReason.authCooldown };
        }

        const reason = !expiration
          ? 'no expiration set (SW possibly restarted)'
          : !hasToken
            ? 'token missing in memory'
            : 'token expiring soon';
        // Expected after every worker restart: the token lives in memory only.
        debugLog(`SW token refresh needed: ${reason}`, {
          expiresInMs: expiration ? expiration - Date.now() : null,
          hasToken,
        });
        try {
          await tokenRequester.onTokenExpired();
        } catch (error: unknown) {
          if (shouldEnterAuthCooldownOnTokenRequestError(error)) {
            const message =
              error instanceof Error ? error.message : 'Token refresh failed';
            enterAuthCooldown(message);
          }
          if (hasUsableInMemoryToken(hasToken, expiration)) {
            return await readStoredConfig();
          }
          return { config: undefined, reason: ConfigUnavailableReason.tokenRequestFailed };
        }
      }

      return await readStoredConfig();
    } finally {
      inflightConfig = null;
    }
  })();
  return inflightConfig;
}

/** The stored config once the worker holds a valid token; `undefined` when it cannot (see `resolveAuthenticatedConfig`). */
export async function ensureAuthenticatedConfig(): Promise<ConfigData | undefined> {
  return (await resolveAuthenticatedConfig()).config;
}

/**
 * The session token in memory; empty before `ensureAuthenticatedConfig()` got
 * one and after a logout. A request authorized by the session is not sent
 * without it (`pull` refuses it).
 */
export function getToken(): string {
  return tokenHolder.getToken();
}

/** test-only */
export function resetAuthCooldownForTests(): void {
  clearAuthCooldown();
}

/** test-only */
export function destroyTokenHolderForTests(): void {
  tokenHolder.destroy();
}
